import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Plus, X, Upload, Save, ArrowLeft, Library } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";

import { useToast } from "../../hooks/use-toast";
import { Link } from "react-router-dom";
// Icon options for issue types
const ICON_OPTIONS = [
  { value: "zap", label: "Lightning (Electrical)", icon: "⚡" },
  { value: "droplets", label: "Water Damage", icon: "💧" },
  { value: "monitor", label: "Screen Issues", icon: "🖥️" },
  { value: "smartphone", label: "Phone Issues", icon: "📱" },
  { value: "cpu", label: "Hardware", icon: "🔧" },
  { value: "wifi", label: "Connectivity", icon: "📶" },
  { value: "battery", label: "Power/Battery", icon: "🔋" },
  { value: "thermometer", label: "Temperature", icon: "🌡️" },
  { value: "volume-2", label: "Audio", icon: "🔊" },
  { value: "camera", label: "Camera", icon: "📷" },
  { value: "settings", label: "General", icon: "⚙️" },
  { value: "alert-triangle", label: "Warning", icon: "⚠️" },
];

const createPlanSchema = z.object({
  planTitle: z.string().min(1, "Plan title is required"),
  planDescription: z.string().min(1, "Plan description is required"),
  systemInstructions: z.string().optional(),
  termsAndConditions: z.string().optional(),
});
function CreateProtectionPlanTemplate() {
  const [issueTypes, setIssueTypes] = useState([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [selectedCoverageLevel, setSelectedCoverageLevel] = useState(null);
  const [customProductType, setCustomProductType] = useState("");
  const [customCoverageLevel, setCustomCoverageLevel] = useState({
    name: "",
    description: "",
    color: "blue",
  });
  const [newIssueType, setNewIssueType] = useState({
    label: "",
    icon: "",
    aiContext: "",
  });
  const [termsFile, setTermsFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const predefinedProductTypes = [
    { id: "laptop", name: "Laptop", category: "Electronics" },
    { id: "phone", name: "Smartphone", category: "Electronics" },
    { id: "tablet", name: "Tablet", category: "Electronics" },
    { id: "appliance", name: "Kitchen Appliance", category: "Home" },
    { id: "gaming", name: "Gaming Console", category: "Electronics" },
    { id: "wearable", name: "Wearable Device", category: "Electronics" },
    { id: "audio", name: "Audio Equipment", category: "Electronics" },
    { id: "camera", name: "Camera", category: "Electronics" },
  ];

  const predefinedCoverageLevels = [
    {
      id: "basic",
      name: "Basic Protection",
      description: "Essential coverage for common issues",
      color: "blue",
    },
    {
      id: "extended",
      name: "Extended Protection",
      description: "Comprehensive coverage with additional benefits",
      color: "purple",
    },
    {
      id: "premium",
      name: "Premium Protection",
      description: "Full coverage with premium services and priority support",
      color: "amber",
    },
  ];

  const form = useForm({
    resolver: zodResolver(createPlanSchema),
    defaultValues: {
      planTitle: "",
      planDescription: "",
      systemInstructions: "",
      termsAndConditions: "",
    },
  });

  const addIssueType = () => {
    if (!newIssueType.label || !newIssueType.icon) {
      toast({
        title: "Missing Information",
        description: "Please provide both label and icon for the issue type.",
        variant: "destructive",
      });
      return;
    }

    const issueType = {
      id: `issue_${Date.now()}`,
      label: newIssueType.label,
      icon: newIssueType.icon,
      aiContext:
        newIssueType.aiContext ||
        `Handle ${newIssueType.label.toLowerCase()} related issues with appropriate troubleshooting steps.`,
    };

    setIssueTypes([...issueTypes, issueType]);
    setNewIssueType({ label: "", icon: "", aiContext: "" });
  };

  const removeIssueType = (id) => {
    setIssueTypes(issueTypes.filter((type) => type.id !== id));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setTermsFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result;
        form.setValue("termsAndConditions", content);
      };
      reader.readAsText(file);
    }
  };

  const onSubmit = async (values) => {
    setIsCreating(true);
    try {
      const planId = `CP-${new Date().getFullYear()}-${Date.now()
        .toString()
        .slice(-6)}`;
      const planData = {
        planId,
        planTitle: values.planTitle,
        planDescription: values.planDescription,
        productType:
          selectedProductTypes.map((pt) => pt.name).join(", ") || "Electronics",
        coverageType: selectedCoverageLevel?.name || "Basic Protection",
        systemInstructions: values.systemInstructions,
        termsAndConditions: values.termsAndConditions,
        issueTypes: issueTypes,
        productName: `${values.planTitle} - Template`,
        purchaseDate: new Date(),
        expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        purchasePrice: "0.00",
        isActive: true,
      };

      const response = await fetch("/api/protection-plans", {
        method: "POST",
        body: JSON.stringify(planData),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast({
        title: "Success!",
        description: `Protection plan ${planId} created successfully.`,
      });

      //   onBack();
    } catch (error) {
      console.error("Failed to create protection plan:", error);
      toast({
        title: "Error",
        description: "Failed to create protection plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/protection-plan-templates">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Create New Protection Plan
            </h1>
            <p className="text-gray-600">
              Define a new protection plan template with custom issue types and
              AI instructions
            </p>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Core details for the protection plan template
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="planTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plan Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Premium Electronics Protection"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="planDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plan Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe what this protection plan covers..."
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <label className="text-sm font-medium text-gray-900 block mb-2">
                      Product Categories
                    </label>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        {predefinedProductTypes.map((productType) => (
                          <div
                            key={productType.id}
                            className="flex items-center space-x-2"
                          >
                            <input
                              type="checkbox"
                              id={productType.id}
                              checked={selectedProductTypes.some(
                                (pt) => pt.id === productType.id
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProductTypes([
                                    ...selectedProductTypes,
                                    productType,
                                  ]);
                                } else {
                                  setSelectedProductTypes(
                                    selectedProductTypes.filter(
                                      (pt) => pt.id !== productType.id
                                    )
                                  );
                                }
                              }}
                              className="rounded border-gray-300 text-accent focus:ring-accent"
                            />
                            <label
                              htmlFor={productType.id}
                              className="text-sm text-gray-700"
                            >
                              {productType.name}
                            </label>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center space-x-2">
                        <Input
                          placeholder="Add custom product type..."
                          value={customProductType}
                          onChange={(e) => setCustomProductType(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            const trimmed = customProductType.trim();
                            if (trimmed) {
                              const newProductType = {
                                id: trimmed.toLowerCase().replace(/\s+/g, "-"),
                                name: trimmed,
                                category: "Custom",
                              };
                              setSelectedProductTypes([
                                ...selectedProductTypes,
                                newProductType,
                              ]);
                              setCustomProductType("");
                            }
                          }}
                          disabled={!customProductType.trim()}
                        >
                          Add
                        </Button>
                      </div>

                      {selectedProductTypes.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedProductTypes.map((productType) => (
                            <Badge
                              key={productType.id}
                              variant="secondary"
                              className="flex items-center gap-1"
                            >
                              {productType.name}
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedProductTypes(
                                    selectedProductTypes.filter(
                                      (pt) => pt.id !== productType.id
                                    )
                                  )
                                }
                                className="ml-1 text-gray-500 hover:text-gray-700"
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-900 block mb-2">
                      Coverage Level
                    </label>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-3">
                        {predefinedCoverageLevels.map((coverage) => (
                          <div
                            key={coverage.id}
                            className={`border rounded-lg p-3 cursor-pointer transition-all ${
                              selectedCoverageLevel?.id === coverage.id
                                ? "border-accent bg-accent/5"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() => setSelectedCoverageLevel(coverage)}
                          >
                            <div className="flex items-center space-x-3">
                              <input
                                type="radio"
                                id={coverage.id}
                                name="coverageLevel"
                                checked={
                                  selectedCoverageLevel?.id === coverage.id
                                }
                                onChange={() =>
                                  setSelectedCoverageLevel(coverage)
                                }
                                className="text-accent focus:ring-accent"
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <div
                                    className={`w-3 h-3 rounded-full bg-${coverage.color}-500`}
                                  ></div>
                                  <label
                                    htmlFor={coverage.id}
                                    className="font-medium text-gray-900 cursor-pointer"
                                  >
                                    {coverage.name}
                                  </label>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {coverage.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center space-x-3 mb-3">
                          <input
                            type="radio"
                            id="custom-coverage"
                            name="coverageLevel"
                            checked={
                              selectedCoverageLevel &&
                              !predefinedCoverageLevels.some(
                                (c) => c.id === selectedCoverageLevel.id
                              )
                            }
                            onChange={() => {
                              if (customCoverageLevel.name) {
                                setSelectedCoverageLevel({
                                  id: "custom",
                                  name: customCoverageLevel.name,
                                  description: customCoverageLevel.description,
                                  color: customCoverageLevel.color,
                                });
                              }
                            }}
                            className="text-accent focus:ring-accent"
                          />
                          <label
                            htmlFor="custom-coverage"
                            className="font-medium text-gray-900"
                          >
                            Custom Coverage Level
                          </label>
                        </div>
                        <div className="space-y-2 ml-6">
                          <Input
                            placeholder="Coverage level name..."
                            value={customCoverageLevel.name}
                            onChange={(e) => {
                              setCustomCoverageLevel({
                                ...customCoverageLevel,
                                name: e.target.value,
                              });
                              if (e.target.value) {
                                setSelectedCoverageLevel({
                                  id: "custom",
                                  name: e.target.value,
                                  description: customCoverageLevel.description,
                                  color: customCoverageLevel.color,
                                });
                              }
                            }}
                          />
                          <Input
                            placeholder="Description..."
                            value={customCoverageLevel.description}
                            onChange={(e) => {
                              setCustomCoverageLevel({
                                ...customCoverageLevel,
                                description: e.target.value,
                              });
                              if (customCoverageLevel.name) {
                                setSelectedCoverageLevel({
                                  id: "custom",
                                  name: customCoverageLevel.name,
                                  description: e.target.value,
                                  color: customCoverageLevel.color,
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Issue Types Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Issue Types</CardTitle>
                  <CardDescription>
                    Define the types of issues this plan covers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Label>Add Issue Type</Label>
                    <div className="space-y-2">
                      <Input
                        placeholder="Issue type label (e.g., Screen Damage)"
                        value={newIssueType.label}
                        onChange={(e) =>
                          setNewIssueType({
                            ...newIssueType,
                            label: e.target.value,
                          })
                        }
                      />
                      <Select
                        value={newIssueType.icon}
                        onValueChange={(value) =>
                          setNewIssueType({ ...newIssueType, icon: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select icon" />
                        </SelectTrigger>
                        <SelectContent>
                          {ICON_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <span className="flex items-center gap-2">
                                <span>{option.icon}</span>
                                <span>{option.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Textarea
                        placeholder="AI context instructions for this issue type..."
                        value={newIssueType.aiContext}
                        onChange={(e) =>
                          setNewIssueType({
                            ...newIssueType,
                            aiContext: e.target.value,
                          })
                        }
                        rows={2}
                      />
                      <Button type="button" onClick={addIssueType} size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Issue Type
                      </Button>
                    </div>
                  </div>

                  {issueTypes.length > 0 && (
                    <div className="space-y-2">
                      <Label>Configured Issue Types</Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {issueTypes.map((type) => (
                          <div
                            key={type.id}
                            className="flex items-center justify-between p-2 border rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">
                                {
                                  ICON_OPTIONS.find(
                                    (opt) => opt.value === type.icon
                                  )?.icon
                                }{" "}
                                {type.label}
                              </Badge>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeIssueType(type.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Terms & Conditions */}
              <Card>
                <CardHeader>
                  <CardTitle>Terms & Conditions</CardTitle>
                  <CardDescription>
                    Upload or enter the terms and conditions for this plan
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="terms-upload">Upload T&Cs File</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="terms-upload"
                        type="file"
                        accept=".txt,.pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          document.getElementById("terms-upload")?.click()
                        }
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </Button>
                      {termsFile && (
                        <span className="text-sm text-gray-600">
                          {termsFile.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="termsAndConditions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Terms & Conditions Text</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter or paste terms and conditions..."
                            rows={8}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* AI System Instructions */}
              <Card>
                <CardHeader>
                  <CardTitle>AI System Instructions</CardTitle>
                  <CardDescription>
                    Custom instructions for AI when handling claims for this
                    plan
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="systemInstructions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>System Instructions</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="e.g., For this premium plan, prioritize customer satisfaction. Allow up to 3 repair attempts before offering replacement. Consider expedited processing for business customers..."
                            rows={8}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline">
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? (
                  <>Creating...</>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Protection Plan
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
}
export default CreateProtectionPlanTemplate;
